import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { Button } from "@faire/web--source/slate/Button";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { FaireMarketTheme } from "@faire/web--source/slate/Theme/EventsTheme";
import { getEventTheme } from "@faire/web--source/slate/Theme/eventsThemeColors";
import { EventsThemeShape } from "@faire/web--source/slate/Theme/EventsThemeShape";
import { EventsThemeShapeCombination } from "@faire/web--source/slate/Theme/EventsThemeShapeCombination";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web--source/slate/Typography";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import React from "react";
import styled from "styled-components";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { useInsiderState } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderState";
import { RetailerSegment } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
export var HomeFaireMarketPreshowBanner = function() {
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onCTAClick = _React_useContext.onCTAClick, requestId = _React_useContext.requestId;
    var _useMarketStore = useMarketStore([
        "retailerSegment",
        "formattedEarlyAccessStartDay"
    ]), retailerSegment = _useMarketStore.retailerSegment, formattedEarlyAccessStartDay = _useMarketStore.formattedEarlyAccessStartDay;
    var insiderMembershipState = useRetailerStore([
        "insiderMembershipState"
    ]).insiderMembershipState;
    var isEurope = useRetailerStore([
        "isEurope"
    ]).isEurope;
    var isInsiderPaused = useInsiderState().isInsiderPaused;
    var cta_link = layoutElement.cta_link, cta_text = layoutElement.cta_text, on_click_event_name = layoutElement.on_click_event_name, main_text = layoutElement.main_text, description_text = layoutElement.description_text, tagline_text = layoutElement.tagline_text;
    var onClick = function() {
        onCTAClick === null || onCTAClick === void 0 ? void 0 : onCTAClick(on_click_event_name !== null && on_click_event_name !== void 0 ? on_click_event_name : "HomeFaireMarketPreshowBanner", requestId);
    };
    var isInsiderLayout = function() {
        return retailerSegment === RetailerSegment.INSIDER_ENROLLED;
    };
    var colors = isInsiderLayout() ? {
        backgroundColor: Decorative.shade.teal,
        hoverColor: getEventTheme("faireMarket").decorative6,
        textColor: getEventTheme("faireMarket").promoTextInverse
    } : {
        backgroundColor: getEventTheme("faireMarket").primaryDark,
        textColor: getEventTheme("faireMarket").promoTextInverse
    };
    var preambleDate = function() {
        return formattedEarlyAccessStartDay;
    };
    var renderPreamble = function() {
        if (insiderMembershipState === IRetailer.InsiderMembershipState.UNPAID) {
            return null;
        }
        var preambleFragment = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "2x"
                }),
                /*#__PURE__*/ _jsx(VerticalLine, {
                    $isInsiderEnrolled: isInsiderLayout()
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "2x"
                }),
                preambleDate()
            ]
        });
        switch(retailerSegment){
            case RetailerSegment.INSIDER_ENROLLED:
            case RetailerSegment.INSIDER_ELIGIBLE:
            case RetailerSegment.INSIDER_CHURNED:
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            children: [
                                /*#__PURE__*/ _jsx(Insider, {
                                    height: "26px",
                                    color: getEventTheme("faireMarket").promoTextInverse
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    size: "1x"
                                }),
                                /*#__PURE__*/ _jsx(Text, {
                                    variant: "subheadingSansMedium",
                                    $isInsiderEnrolled: isInsiderLayout(),
                                    children: /*#__PURE__*/ _jsxs(Row, {
                                        children: [
                                            tagline_text,
                                            preambleFragment
                                        ]
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "2x"
                        })
                    ]
                });
            default:
                return null;
        }
    };
    if (isInsiderPaused && isEurope) {
        return null;
    }
    var isInsiderEnrolled = retailerSegment === RetailerSegment.INSIDER_ENROLLED;
    var _getEventTheme_decorative6, _getEventTheme_decorative5;
    return /*#__PURE__*/ _jsx(ThemeProvider, {
        theme: FaireMarketTheme,
        children: /*#__PURE__*/ _jsxs(Wrapper, {
            $backgroundColor: colors.backgroundColor,
            children: [
                isInsiderEnrolled ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(InsiderFaireMarketIcon, {
                            color: getEventTheme("faireMarket").decorative5
                        }),
                        /*#__PURE__*/ _jsx(StyledMarketSecondaryShape, {
                            color: getEventTheme("faireMarket").primaryLight
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                    mobile: /*#__PURE__*/ _jsx(MobileFaireMarketIcon, {
                        color: getEventTheme("faireMarket").badgeIcon,
                        $isNotEligible: retailerSegment === RetailerSegment.NOT_INSIDER
                    }),
                    tabletAndAbove: /*#__PURE__*/ _jsx(StyledMarketCombinationShape, {
                        width: "446px",
                        height: "281px",
                        frontShapeColor: (_getEventTheme_decorative6 = getEventTheme("faireMarket").decorative6) !== null && _getEventTheme_decorative6 !== void 0 ? _getEventTheme_decorative6 : "",
                        backShapeColor: (_getEventTheme_decorative5 = getEventTheme("faireMarket").decorative5) !== null && _getEventTheme_decorative5 !== void 0 ? _getEventTheme_decorative5 : ""
                    })
                }),
                /*#__PURE__*/ _jsxs(Content, {
                    children: [
                        renderPreamble(),
                        /*#__PURE__*/ _jsx(Title, {
                            variant: {
                                tabletAndAbove: "displayMSerifRegular",
                                mobileAndAbove: "displaySSerifRegular"
                            },
                            $isInsiderEnrolled: isInsiderLayout(),
                            children: main_text
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "2x"
                        }),
                        /*#__PURE__*/ _jsx(Subtitle, {
                            variant: {
                                tabletAndAbove: "subheadingSansRegular",
                                mobileAndAbove: "paragraphSansRegular"
                            },
                            $isInsiderEnrolled: isInsiderLayout(),
                            children: description_text
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "3x"
                        }),
                        cta_text && cta_link ? /*#__PURE__*/ _jsx(StyledCTA, {
                            linkTo: cta_link,
                            $hoverColor: colors.hoverColor,
                            $textColor: colors.textColor,
                            onClick: onClick,
                            children: cta_text
                        }) : null
                    ]
                })
            ]
        })
    });
};
var Wrapper = styled.div.withConfig({
    componentId: "sc-9c2a6f9c-0"
})([
    "background-color:",
    ";position:relative;padding:",
    ";overflow:hidden;max-width:1280px;margin:0 auto;border-radius:",
    ";",
    "{padding:",
    ";margin:",
    ";}",
    "{padding:",
    ";border-radius:0;}"
], function(param) {
    var $backgroundColor = param.$backgroundColor;
    return $backgroundColor ? $backgroundColor : null;
}, getSpacing("7x", "8x"), getSpacing("1x"), responsiveMediaQueries.tablet, getSpacing("7x", "8x"), getSpacing(0, "7x"), responsiveMediaQueries.mobile, getSpacing("4x", "3x"));
var StyledMarketCombinationShape = styled(EventsThemeShapeCombination.faireMarket.comboB).withConfig({
    componentId: "sc-9c2a6f9c-1"
})([
    "position:absolute;bottom:",
    ";right:",
    ";"
], getSpacing("-6x"), getSpacing("-10x"));
var FaireMarketIcon = styled(EventsThemeShape.faireMarket.badge).withConfig({
    componentId: "sc-9c2a6f9c-2"
})([
    "position:absolute;height:auto;"
]);
var InsiderFaireMarketIcon = styled(FaireMarketIcon).attrs({
    color: getEventTheme("faireMarket").decorative4
}).withConfig({
    componentId: "sc-9c2a6f9c-3"
})([
    "right:98px;top:41px;width:96px;",
    "{display:none;}"
], responsiveMediaQueries.mobile);
var MobileFaireMarketIcon = styled(FaireMarketIcon).attrs({
    width: "88px"
}).withConfig({
    componentId: "sc-9c2a6f9c-4"
})([
    "position:absolute;right:0px;top:",
    ";"
], function(param) {
    var $isNotEligible = param.$isNotEligible;
    return $isNotEligible ? "-18px" : "24px";
});
var StyledMarketSecondaryShape = styled(EventsThemeShape.faireMarket.secondary).attrs({
    color: getEventTheme("faireMarket").decorative2
}).withConfig({
    componentId: "sc-9c2a6f9c-5"
})([
    "position:absolute;width:139px;height:122px;top:",
    ";right:-35px;",
    "{width:224px;height:197px;top:unset;bottom:-7px;right:",
    ";}"
], getSpacing("-2x"), responsiveMediaQueries.tabletAndAbove, getSpacing("-6x"));
var Content = styled(Column).attrs({
    align: "start"
}).withConfig({
    componentId: "sc-9c2a6f9c-6"
})([
    "position:relative;",
    "{max-width:calc(100% - 130px);}",
    "{max-width:calc(100% - 179px);}"
], responsiveMediaQueries.desktopAndAbove, responsiveMediaQueries.tablet);
var Text = styled(Typography).withConfig({
    componentId: "sc-9c2a6f9c-7"
})([
    "&&&{color:",
    ";}"
], function(param) {
    var $isInsiderEnrolled = param.$isInsiderEnrolled;
    return $isInsiderEnrolled ? getEventTheme("faireMarket").promoTextInverse : getEventTheme("faireMarket").promoTextInverse;
});
var Title = styled(Text).withConfig({
    componentId: "sc-9c2a6f9c-8"
})([
    "&&&{",
    "{padding-right:",
    ";}width:100%;}"
], responsiveMediaQueries.mobile, getSpacing("10x"));
var Subtitle = styled(Text).withConfig({
    componentId: "sc-9c2a6f9c-9"
})([
    "max-width:730px;",
    "{max-width:434px;}",
    "{max-width:100%;}"
], responsiveMediaQueries.tablet, responsiveMediaQueries.mobile);
var StyledCTA = styled(Button).withConfig({
    componentId: "sc-9c2a6f9c-10"
})([
    "background:",
    ";color:",
    ";padding:",
    ";min-width:200px;&:hover,&:focus{background-color:",
    ";color:",
    ";}",
    "{width:100%;}"
], Core.surface.inverse, Core.text.default, getSpacing("3x"), function(props) {
    return props.$hoverColor;
}, function(props) {
    return props.$textColor;
}, responsiveMediaQueries.mobile);
var VerticalLine = styled.div.withConfig({
    componentId: "sc-9c2a6f9c-11"
})([
    "height:26px;border-right:1px solid ",
    ";"
], function(param) {
    var $isInsiderEnrolled = param.$isInsiderEnrolled;
    return $isInsiderEnrolled ? getEventTheme("faireMarket").promoTextInverse : getEventTheme("faireMarket").promoTextInverse;
});
